import React, { useState, useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMapMarkerAlt, faSearch, faUser, faHeart, faShoppingBag, faChevronDown, faRightFromBracket, faTruck, faStore, faCheck } from '@fortawesome/free-solid-svg-icons';
import { API_BASE_URL } from '../config/api.config';
import './navbar.css';

const Navbar = ({
  user,
  onCartClick,
  onAccountClick,
  onFavoritesClick,
  onHomeClick,
  onLogout,
  onShippingClick,
  showShippingModal,
  onCloseShippingModal,
  onProductSelect,
  shippingMethod,
  onShippingMethodSelect,
  cartCount = 0,
}) => {
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const profileRef = useRef(null);
  const searchRef = useRef(null);

  const [allProducts, setAllProducts] = useState([]);
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (profileRef.current && !profileRef.current.contains(e.target)) {
        setShowProfileMenu(false);
      }
      if (searchRef.current && !searchRef.current.contains(e.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Load product names for search (lightweight — no image blobs)
  useEffect(() => {
    let cancelled = false;
    fetch(`${API_BASE_URL}?action=products`)
      .then((res) => res.json())
      .then((data) => {
        if (!cancelled && data.status === 'ok') {
          const products = (data.products || []).map((p) => ({
            id: p.id,
            name: p.name,
            description: p.description,
            price: p.price,
            categoryId: p.category_id,
            image: p.image || (p.has_image_data
              ? `${API_BASE_URL}?action=product_image&productId=${p.id}`
              : '/images/default.jpg'),
          }));
          setAllProducts(products);
        }
      })
      .catch((err) => console.error('Error loading products for search:', err));
    return () => {
      cancelled = true;
    };
  }, []);

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setQuery(value);
    const term = value.trim().toLowerCase();
    if (!term) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }
    const matches = allProducts
      .filter(
        (p) =>
          (p.name && p.name.toLowerCase().includes(term)) ||
          (p.description && p.description.toLowerCase().includes(term))
      )
      .slice(0, 8);
    setSuggestions(matches);
    setShowSuggestions(true);
  };

  const handleSelectSuggestion = (product) => {
    setShowSuggestions(false);
    setQuery('');
    if (onProductSelect) onProductSelect(product);
  };

  const handleLogout = () => {
    setShowProfileMenu(false);
    if (onLogout) onLogout();
  };

  return (
    <header className="navbar top-navbar">
      <button type="button" className="navbar-left navbar-brand-link" onClick={onHomeClick}>
        <img src="/images/logo.jpeg" alt="AlamCareRx Logo" className="navbar-brand-logo" />
        <div className="navbar-brand-text">
          <span className="brand-name">AlamCareRx</span>
        </div>
      </button>

      <button className="shipping-button" onClick={onShippingClick}>
        <FontAwesomeIcon icon={shippingMethod === 'pickup' ? faStore : faMapMarkerAlt} />
        <span>
          {shippingMethod === 'pickup'
            ? 'Store pickup'
            : shippingMethod === 'delivery'
            ? 'Home delivery'
            : 'Delivery method'}
        </span>
        <FontAwesomeIcon icon={faChevronDown} />
      </button>

      <div className="navbar-center">
        <div className="search-wrapper" ref={searchRef}>
          <FontAwesomeIcon icon={faSearch} className="search-icon" />
          <input
            type="text"
            placeholder="Search Beauty, Personal Care, Supplements, Brands..."
            className="search-input"
            value={query}
            onChange={handleSearchChange}
            onFocus={() => query.trim() && setShowSuggestions(true)}
          />
          {showSuggestions && (
            <div className="search-suggestions">
              {suggestions.length > 0 ? (
                suggestions.map((product) => (
                  <button
                    type="button"
                    key={product.id}
                    className="search-suggestion"
                    onClick={() => handleSelectSuggestion(product)}
                  >
                    <img src={product.image} alt={product.name} className="search-suggestion-img" />
                    <div className="search-suggestion-info">
                      <span className="search-suggestion-name">{product.name}</span>
                      {product.description && (
                        <span className="search-suggestion-desc">{product.description}</span>
                      )}
                    </div>
                    <span className="search-suggestion-price">Rs. {product.price}</span>
                  </button>
                ))
              ) : (
                <div className="search-no-results">No products found for "{query}"</div>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="navbar-right">
        {user ? (
          <div className="profile-menu-container" ref={profileRef}>
            <button
              type="button"
              className="icon-button profile-icon-btn"
              onClick={() => setShowProfileMenu(!showProfileMenu)}
              title="My Profile"
            >
              <FontAwesomeIcon icon={faUser} />
            </button>
            {showProfileMenu && (
              <div className="profile-dropdown">
                <div className="profile-dropdown-header">
                  <FontAwesomeIcon icon={faUser} className="profile-dropdown-avatar" />
                  <div>
                    <p className="profile-dropdown-name">{user.name}</p>
                    <p className="profile-dropdown-email">{user.email}</p>
                  </div>
                </div>
                <button type="button" className="profile-dropdown-logout" onClick={handleLogout}>
                  <FontAwesomeIcon icon={faRightFromBracket} /> Logout
                </button>
              </div>
            )}
          </div>
        ) : (
          <>
            <button className="auth-button" onClick={onAccountClick}>Login & Register</button>
            <button className="icon-button auth-icon-btn" onClick={onAccountClick} title="Login & Register">
              <FontAwesomeIcon icon={faUser} />
            </button>
          </>
        )}
        <button className="icon-button" onClick={onFavoritesClick} title="Favourites">
          <FontAwesomeIcon icon={faHeart} />
        </button>
        <button className="icon-button cart-icon-btn" onClick={onCartClick} title="Cart">
          <FontAwesomeIcon icon={faShoppingBag} />
          {cartCount > 0 && <span className="cart-badge">{cartCount}</span>}
        </button>
      </div>

      {showShippingModal && (
        <div className="shipping-modal-overlay" onClick={onCloseShippingModal}>
          <div className="shipping-modal" onClick={(e) => e.stopPropagation()}>
            <button className="close-btn" onClick={onCloseShippingModal}>✕</button>
            <h3>Select your shipping method</h3>
            <p>Choose home delivery or store pickup.</p>
            <div className="shipping-options">
              <button
                type="button"
                className={`shipping-option ${shippingMethod === 'delivery' ? 'selected' : ''}`}
                onClick={() => onShippingMethodSelect && onShippingMethodSelect('delivery')}
              >
                {shippingMethod === 'delivery' && (
                  <span className="shipping-option-check"><FontAwesomeIcon icon={faCheck} /></span>
                )}
                <div className="option-icon"><FontAwesomeIcon icon={faTruck} /></div>
                <h4>Home delivery</h4>
                <p>As fast as 2 hours*</p>
              </button>
              <button
                type="button"
                className={`shipping-option ${shippingMethod === 'pickup' ? 'selected' : ''}`}
                onClick={() => onShippingMethodSelect && onShippingMethodSelect('pickup')}
              >
                {shippingMethod === 'pickup' && (
                  <span className="shipping-option-check"><FontAwesomeIcon icon={faCheck} /></span>
                )}
                <div className="option-icon"><FontAwesomeIcon icon={faStore} /></div>
                <h4>Store pickup</h4>
                <p>Collect from branch nearby</p>
              </button>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Navbar;
